/* Cart-independent display estimates only. Shopify owns cart/checkout totals. */
(function (root) {
  // Parse raw Regios rules only in the theme. The backend returns the original
  // objects; unsupported pricing rules must not be interpreted as unrestricted.
  "use strict";

  // Extract the raw discount value, plus its template when it is a price list,
  // so support filters check both levels without traversing individual rows.
  const discountValues = (d) => {
    const value = record(d.logic.entryPoint.then.value);
    return value.type === "PRICE_LIST"
      ? [value, record(value.discountTemplate)]
      : [value];
  };

  // Reuse this filter when calculating cached campaigns, which can expire
  // between requests. No timer: the check runs whenever prices are calculated.
  const activeDiscountFilter = {
    reason: "Discount is inactive or has an invalid schedule",
    accepts: (d) => {
      const now = Date.now();
      return (
        typeof d.startsAt === "string" &&
        Date.parse(d.startsAt) <= now &&
        (d.endsAt == null ||
          (typeof d.endsAt === "string" && Date.parse(d.endsAt) > now))
      );
    },
  };

  // Campaign support rules: every filter must pass. Keep these in one place
  // when adjusting scope. The parsers below still validate IDs, amounts and shapes.
  // Usage caps, per-customer usage and additional shipping logic intentionally
  // do not block product-price display. Checkout enforces them independently.
  const discountFilters = [
    activeDiscountFilter,
    {
      reason: "Automatic discounts only",
      accepts: (d) => d.discountMethod === "AUTOMATIC",
    },
    {
      reason: "Amount-off-products discounts only",
      accepts: (d) => d.discountType === "amount-off-products",
    },
    {
      reason: "Product discount class only",
      accepts: (d) =>
        Array.isArray(d.discountClasses) &&
        d.discountClasses.length === 1 &&
        d.discountClasses[0] === "PRODUCT",
    },
    {
      reason: "Unsupported sale-price handling",
      accepts: (d) =>
        ["APPLY_TO_COMPARE_AT_PRICE", "STACK", "SKIP"].includes(
          d.salePriceHandlingStrategy,
        ),
    },
    {
      reason: "Unsupported logic",
      accepts: (d) => d.logic?.type === "ROOT",
    },
    {
      reason: "Unsupported branches",
      accepts: (d) => {
        const node = d.logic?.entryPoint;
        // Supported flow: IF all conditions match, THEN apply one discount.
        // Example:
        // IF:
        //   Customer belongs to the Pro segment
        //   AND product belongs to the selected collection
        // THEN:
        //   Apply 20% off
        // ELSE:
        //   Nothing
        // OR conditions, nested decisions and ELSE offers need additional logic.
        return (
          node?.type === "CHECK_IF" &&
          node.matchType === "AND" &&
          node.otherwise == null &&
          node.then?.type === "APPLY_DISCOUNT" &&
          Array.isArray(node.conditions)
        );
      },
    },
    {
      reason: "Unsupported exclusion",
      accepts: (d) =>
        d.logic.entryPoint.conditions.every(
          (c) => c?.includeOrExclude === "INCLUDE",
        ),
    },
    {
      reason: "Unsupported condition",
      accepts: (d) =>
        d.logic.entryPoint.conditions.every(
          (c) =>
            (c.type === "CUSTOMER_ELIGIBILITY" &&
              c.eligibility?.type === "SPECIFIC_CUSTOMER_SEGMENTS") ||
            (c.type === "PRODUCT_ELIGIBILITY" &&
              c.eligibility?.type === "SPECIFIC_COLLECTIONS"),
        ),
    },
    {
      reason: "Customer segment required",
      accepts: (d) =>
        d.logic.entryPoint.conditions.some(
          (c) => c.type === "CUSTOMER_ELIGIBILITY",
        ),
    },
    {
      reason: "Unsupported discount value type",
      accepts: (d) =>
        discountValues(d).every((v) =>
          ["PRICE_LIST", "PERCENTAGE", "FIXED_PRICE", "FIXED_AMOUNT"].includes(
            v.type,
          ),
        ),
    },
    {
      reason: "Nested price list",
      accepts: (d) =>
        discountValues(d).every(
          (v, index) => index === 0 || v.type !== "PRICE_LIST",
        ),
    },
    {
      reason: "Cart-dependent value",
      accepts: (d) =>
        discountValues(d).every(
          (v) => !v.quantity && v.maxDiscountAmount == null,
        ),
    },
    {
      reason: "Unsupported price-list resource",
      accepts: (d) => {
        const value = d.logic.entryPoint.then.value;
        return (
          value.type !== "PRICE_LIST" ||
          Object.values(record(value.data)).every((row) =>
            ["PRODUCT_ID", "VARIANT_ID", "COLLECTION_ID"].includes(
              value.allRowsAreThisType || record(row).resourceType,
            ),
          )
        );
      },
    },
    {
      reason: "Unsupported amount",
      accepts: (d) =>
        discountValues(d).every(
          (v) =>
            !["FIXED_PRICE", "FIXED_AMOUNT"].includes(v.type) ||
            (v.onlyApplyDiscountOncePerOrder === false &&
              (!v.appliesTo || v.appliesTo === "INDIVIDUAL_LINE_ITEMS")),
        ),
    },
    {
      reason: "Unsupported rounding",
      accepts: (d) =>
        discountValues(d).every(
          (v) =>
            !["FIXED_PRICE", "FIXED_AMOUNT"].includes(v.type) ||
            ["NONE", "CEIL", "FLOOR", "ROUND"].includes(
              v.roundingType || "NONE",
            ),
        ),
    },
  ];

  // Require a non-null object (not an array) before reading its fields.
  // Return the original object unchanged; malformed input throws.
  const record = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value))
      throw Error("Invalid object");
    return value;
  };
  // Parse comma-separated numeric IDs, e.g. "123, 456" -> ["123", "456"].
  // Keep IDs as strings to avoid precision loss; reject empty or invalid lists.
  const ids = (value) => {
    const list = String(value ?? "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    if (!list.length || list.some((x) => !/^\d+$/.test(x)))
      throw Error("Invalid IDs");
    return list;
  };
  // Convert a non-negative number or decimal string into a finite number.
  // Reject empty strings, negative values and malformed amounts instead of coercing them.
  const number = (value) => {
    if (
      !["string", "number"].includes(typeof value) ||
      (typeof value === "string" && !/^\d+(?:\.\d+)?$/.test(value.trim())) ||
      !Number.isFinite(Number(value)) ||
      Number(value) < 0
    )
      throw Error("Invalid value");
    return Number(value);
  };
  // Missing stacking tags mean no tags. Otherwise require an array of strings,
  // preserving the exact tag names used to check reciprocal stacking permission.
  const tags = (value) => {
    if (value == null) return [];
    if (!Array.isArray(value) || value.some((tag) => typeof tag !== "string"))
      throw Error("Invalid stacking tags");
    return value;
  };
  // Convert a supported raw value into the calculator's numeric rule, including
  // price-list rows. Fixed amounts stay in currency units; percentages stay 0–100.
  function valueRule(input) {
    // discountFilters has already checked support; only parse and validate data here.
    const v = record(input);
    if (v.type === "PRICE_LIST") {
      const template = valueRule(v.discountTemplate);
      const entries = Object.entries(record(v.data)).map(([id, row]) => {
        ids(id);
        const item = v.allRowsAreThisType ? null : record(row);
        const resource = v.allRowsAreThisType || item?.resourceType;
        const amount = number(v.allRowsAreThisType ? row : item?.discountValue);
        if (template.type === "PERCENTAGE" && amount > 100)
          throw Error("Invalid percentage");
        return { id, resource, amount };
      });
      return { type: "PRICE_LIST", template, entries };
    }
    if (v.type === "PERCENTAGE") {
      const amount = number(v.percentage);
      if (amount > 100) throw Error("Invalid percentage");
      return { type: v.type, amount };
    }
    const rounding = v.roundingType || "NONE";
    return {
      type: v.type,
      amount: number(v.type === "FIXED_PRICE" ? v.price : v.amount),
      rounding,
      convert: v.disableCurrencyConversion !== true,
    };
  }
  // Filter and validate one raw Regios discount, then build a new calculator
  // campaign without changing the input. Throws so the caller can skip it and log why.
  function normalizeDiscount(input) {
    const d = record(input);
    // Stop at the first failed filter; its reason is surfaced in the browser warning.
    const rejected = discountFilters.find((filter) => !filter.accepts(d));
    if (rejected) throw Error(rejected.reason);
    const saleStrategy = d.salePriceHandlingStrategy;
    const startsAt = d.startsAt,
      endsAt = d.endsAt;
    const logic = record(d.logic);
    const node = record(logic.entryPoint),
      then = record(node.then);
    const segments = [],
      products = [];
    for (const item of node.conditions) {
      const condition = record(item),
        e = record(condition.eligibility);
      if (condition.type === "CUSTOMER_ELIGIBILITY") {
        segments.push(ids(e.asListOfIds));
      } else {
        const selected = e.selectedCollections ?? [];
        if (!Array.isArray(selected)) throw Error("Invalid product selection");
        const selectedIds = selected.map((x) =>
          String(record(x).id).split("/").pop(),
        );
        products.push({
          type: e.type,
          ids: ids([e.asListOfIds, ...selectedIds].filter(Boolean).join(",")),
        });
      }
    }
    const combinations = record(d.combinesWith ?? {});
    if (
      typeof d.title !== "string" ||
      (typeof d.id !== "string" && typeof d.id !== "number")
    )
      throw Error("Invalid campaign identity");
    return {
      id: String(d.id),
      title: d.title,
      startsAt,
      endsAt: endsAt ?? null,
      segments,
      products,
      value: valueRule(then.value),
      saleStrategy,
      combines: combinations.productDiscounts === true,
      stackTags: tags(combinations.productDiscountsWithTagsOnSameCartLine),
      tags: tags(d.tags),
    };
  }

  // A small epsilon avoids rounding a cent down because of floating-point noise.
  const round = (value) => Math.round(value + 1e-8);
  // Same-line stacking requires permission in BOTH directions. Allowing product
  // discounts in one order alone does not permit stacking on the same item.
  const stacks = (a, b) =>
    a.combines &&
    b.combines &&
    a.stackTags.some((tag) => b.tags.includes(tag)) &&
    b.stackTags.some((tag) => a.tags.includes(tag));

  // Turn one eligible campaign (d) into a saving for this variant, or null.
  // `fixed` is cents saved; `percentage` is a percent such as 20, not 0.2.
  // Customer eligibility is already checked by the backend; product matching is here.
  function candidate(d, product, variant, rate) {
    // normalizeDiscount already validates the supported condition types.
    // Here we only match collections: all rules, any collection within each rule.
    if (
      !d.products.every((rule) =>
        rule.ids.some((id) => product.collections.map(String).includes(id)),
      )
    )
      return null;
    let value = d.value;
    if (value.type === "PRICE_LIST") {
      // Preserve explicit variant/product overrides; collection rows are fallback.
      // Regios overlap precedence still needs checkout verification.
      let row =
        value.entries.find(
          (row) =>
            row.resource === "VARIANT_ID" && row.id === String(variant.id),
        ) ||
        value.entries.find(
          (row) =>
            row.resource === "PRODUCT_ID" && row.id === String(product.id),
        );
      if (!row) {
        const collections = product.collections.map(String);
        const matches = value.entries.filter(
          (entry) =>
            entry.resource === "COLLECTION_ID" &&
            collections.includes(entry.id),
        );
        // Do not depend on row order when collection prices conflict. Add a
        // tie-break rule only after Regios's checkout behavior is verified.
        if (matches.some((entry) => entry.amount !== matches[0].amount))
          return null;
        row = matches[0];
      }
      if (!row) return null;
      value = { ...value.template, amount: row.amount };
    }
    const price = variant.price;
    const onSale = variant.compare_at_price > price;
    // Honor how the campaign treats an existing Shopify sale: skip it, calculate
    // against compare-at, or apply the discount to the current selling price.
    if (onSale && d.saleStrategy === "SKIP") return null;
    const base =
      onSale && d.saleStrategy === "APPLY_TO_COMPARE_AT_PRICE"
        ? variant.compare_at_price
        : price;
    if (value.type === "PERCENTAGE") {
      // When based on compare-at, express the result as cents saved from the
      // current selling price. Never let an offer increase that selling price.
      if (base !== price)
        return {
          d,
          fixed: Math.max(0, price - round(base * (1 - value.amount / 100))),
          percentage: 0,
        };
      return { d, fixed: 0, percentage: value.amount };
    }
    // Regios rounds the converted final price, not the amount saved. No whole-unit rounding in base currency.
    // Regios fixed values arrive in currency units; variant prices use cents.
    const converted = value.amount * (value.convert ? rate : 1) * 100;
    const roundedPrice = (amount) => {
      if (rate === 1 || value.rounding === "NONE") return round(amount);
      const fn = { CEIL: Math.ceil, FLOOR: Math.floor, ROUND: Math.round }[
        value.rounding
      ];
      return fn(amount / 100) * 100;
    };
    let target;
    if (value.type === "FIXED_PRICE")
      target = value.convert ? roundedPrice(converted) : round(converted);
    else if (value.type === "FIXED_AMOUNT")
      // Round the resulting selling price first, then derive the saving to apply
      // to the campaign's chosen base (selling price or compare-at price).
      target = base - (price - roundedPrice(price - converted));
    else return null;
    // A fixed target of €70 on a €100 item becomes €30 off for stack calculation.
    return { d, fixed: Math.max(0, price - target), percentage: 0 };
  }

  // Return { regular, sale, offers }, with both prices in cents. This function is
  // independent of the DOM, network, and cart; it uses the supplied campaign snapshot.
  function calculate(product, variant, campaigns, rate = 1) {
    const regular = Number(variant?.price);
    const fallback = { regular, sale: regular, offers: [] };
    // Invalid inputs preserve the public price rather than inventing a private price.
    if (
      !Number.isSafeInteger(regular) ||
      regular < 0 ||
      !Number.isFinite(rate) ||
      rate <= 0
    )
      return fallback;
    // Recheck dates even for campaigns restored from session storage.
    const offers = campaigns
      .filter(activeDiscountFilter.accepts)
      .map((d) => candidate(d, product, variant, rate))
      .filter((x) => x && (x.fixed > 0 || x.percentage > 0));
    // ponytail: exhaustive legal subsets for small campaign sets; fail closed above 12, add a bounded solver if needed.
    if (offers.length > 12) return fallback;
    let best = fallback;
    // Explore each permitted subset once. `factor` accumulates percentage
    // multipliers; `fixed` accumulates cents off. This is our stacking model and
    // still needs checkout parity checks for the store's discount configuration.
    function visit(index, selected, factor, fixed) {
      // Example: €100 × 0.8 − €30 = €50. Never display a negative final price.
      const sale = Math.max(0, round(regular * factor) - fixed);
      if (sale < best.sale)
        best = {
          regular,
          sale,
          offers: selected.map((x) => ({ id: x.d.id, title: x.d.title })),
        };
      for (let i = index; i < offers.length; i++) {
        const next = offers[i];
        // The new offer must stack with every selected offer, not just the last.
        // An empty selection accepts any offer, so standalone offers compete too.
        if (selected.every((other) => stacks(other.d, next.d))) {
          visit(
            i + 1,
            [...selected, next],
            factor * (1 - next.percentage / 100),
            fixed + next.fixed,
          );
        }
      }
    }
    visit(0, [], 1, 0);
    return best;
  }
  const api = { calculate, stacks, normalizeDiscount };
  // The same calculator runs in Node regression tests and in the browser theme.
  if (typeof module !== "undefined") module.exports = api;
  else root.PrivateSalesPricing = api;
})(globalThis);
